import { relatedCore, topicNavigation } from "../config/siteNavigation";
import { manualTestingClusterPages } from "./manualTestingCluster";
import { automationTestingClusterPages } from "./automationTestingCluster";
import { apiTestingClusterPages } from "./apiTestingCluster";
import { interviewQuestionsClusterPages } from "./interviewQuestionsCluster";
import { practicalExampleClusterPages } from "./practicalExampleCluster";

const defaultCtas = {
  practice: { label: "Practice Now", to: "/automation-testing" },
  notes: { label: "Download Notes", to: "/study-notes" },
  course: { label: "Join Course", to: "/enroll" },
};

const baseRelated = relatedCore.map((topic) => ({
  title: topic.title,
  to: topic.path,
  category: topic.category,
}));

const manualClusterRelated = manualTestingClusterPages.slice(0, 6).map((page) => ({
  title: page.title,
  to: page.path,
  category: "Manual Testing",
}));

const automationClusterRelated = automationTestingClusterPages.slice(0, 6).map((page) => ({
  title: page.title,
  to: page.path,
  category: "Automation Testing",
}));

const apiClusterRelated = apiTestingClusterPages.slice(0, 6).map((page) => ({
  title: page.title,
  to: page.path,
  category: "API Testing",
}));

const interviewClusterRelated = interviewQuestionsClusterPages.slice(0, 6).map((page) => ({
  title: page.title,
  to: page.path,
  category: "Interview Prep",
}));

const practicalExampleRelated = practicalExampleClusterPages.slice(0, 6).map((page) => ({
  title: page.title,
  to: page.path,
  category: "Practical Examples",
}));

const articleLayoutConfig = {
  "/manual-testing": {
    eyebrow: "Manual Testing Guide",
    title: "Manual Testing Learning Path",
    description: "Use this guide to understand QA fundamentals, test cases, SDLC, STLC, defects, and interview-ready manual testing skills.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Manual Testing", to: "/manual-testing" },
    ],
    toc: [
      { label: "What is Manual Testing?", href: "#manual-testing-definition" },
      { label: "Tester Responsibilities", href: "#tester-responsibilities" },
      { label: "SDLC and STLC", href: "#sdlc-stlc" },
      { label: "Test Cases and Defects", href: "#test-cases-defects" },
    ],
    previous: { label: "Software Testing", to: "/software-testing" },
    next: { label: "Automation Testing", to: "/automation-testing-topic" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/automation-testing-topic": {
    eyebrow: "Automation Testing Guide",
    title: "Automation Testing Learning Path",
    description: "Learn where automation fits, why it matters, and how Selenium, Playwright, frameworks, and CI/CD support faster QA delivery.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Automation Testing", to: "/automation-testing-topic" },
    ],
    toc: [
      { label: "What is Automation Testing?", href: "#automation-definition" },
      { label: "Why Automation Matters", href: "#automation-matters" },
      { label: "Benefits", href: "#automation-benefits" },
      { label: "Tools and Strategy", href: "#automation-tools" },
    ],
    previous: { label: "Manual Testing", to: "/manual-testing" },
    next: { label: "Automation Practice", to: "/automation-testing" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/api-testing": {
    eyebrow: "API Testing Guide",
    title: "API Testing Learning Path",
    description: "Understand APIs, HTTP methods, status codes, authentication, request-response validation, and real-time backend testing scenarios.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "API Testing", to: "/api-testing" },
    ],
    toc: [
      { label: "API Basics", href: "#api-basics" },
      { label: "Architecture Flow", href: "#architecture" },
      { label: "HTTP and HTTPS", href: "#http-https" },
      { label: "Status Codes", href: "#status-codes" },
    ],
    previous: { label: "Automation Testing", to: "/automation-testing-topic" },
    next: { label: "API Interview Questions", to: "/api-testing-interview" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/software-testing": {
    eyebrow: "Software Testing Guide",
    title: "Software Testing Complete Learning Path",
    description: "Explore testing fundamentals, principles, types, levels, techniques, test artifacts, and STLC in one structured guide.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Software Testing", to: "/software-testing" },
    ],
    toc: [
      { label: "What is Software Testing?", href: "#intro" },
      { label: "Testing Principles", href: "#fundamentals" },
      { label: "Types of Testing", href: "#types" },
      { label: "STLC Phases", href: "#stlc" },
    ],
    previous: { label: "Home", to: "/home" },
    next: { label: "Manual Testing", to: "/manual-testing" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/testing-interview-questions": {
    eyebrow: "Interview Preparation",
    title: "Software Testing Interview Preparation",
    description: "Prepare for QA interviews with topic-wise questions, real scenarios, revision notes, and practical learning paths.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Interview Questions", to: "/testing-interview-questions" },
    ],
    toc: [
      { label: "Manual Testing Questions", href: "#manual-questions" },
      { label: "Automation Questions", href: "#automation-questions" },
      { label: "API Questions", href: "#api-questions" },
      { label: "SQL Questions", href: "#sql-questions" },
    ],
    previous: { label: "Study Notes", to: "/study-notes" },
    next: { label: "Join Course", to: "/enroll" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/study-notes": {
    eyebrow: "Study Notes",
    title: "Software Testing Study Notes",
    description: "Use these notes for fast revision, interview preparation, and topic-wise QA learning.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Study Notes", to: "/study-notes" },
    ],
    toc: [
      { label: "Manual Notes", href: "#manual-notes" },
      { label: "Performance Notes", href: "#performance-notes" },
      { label: "API Notes", href: "#api-notes" },
    ],
    previous: { label: "Software Testing", to: "/software-testing" },
    next: { label: "Interview Questions", to: "/testing-interview-questions" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/jira-notes": {
    eyebrow: "Premium JIRA Notes",
    title: "JIRA for QA Learning Pack",
    description: "Structured JIRA notes for QA learners with workflow, boards, JQL, reports, sprint support, and interview answers.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Study Notes", to: "/study-notes" },
      { label: "JIRA Notes", to: "/jira-notes" },
    ],
    toc: [
      { label: "Overview", href: "#overview" },
      { label: "What Learners Get", href: "#includes" },
      { label: "Roadmap", href: "#roadmap" },
      { label: "Images", href: "#images" },
      { label: "Real-Time Experience", href: "#real-time-user-experience" },
      { label: "Detailed Syllabus", href: "#syllabus" },
      { label: "Bug Example", href: "#bug-example" },
      { label: "Advanced JQL", href: "#advanced-jql" },
      { label: "Interview Questions", href: "#interview-questions" },
      { label: "MNC Questions", href: "#mnc-interview-questions" },
      { label: "Bonus", href: "#bonus" },
    ],
    previous: { label: "Study Notes", to: "/study-notes" },
    next: { label: "Join Course", to: "/enroll" },
    related: baseRelated,
    ctas: {
      ...defaultCtas,
      notes: { label: "More Notes", to: "/study-notes" },
    },
  },
  "/testing-blog": {
    eyebrow: "QA Blog",
    title: "Software Testing Blog",
    description: "Read practical software testing articles, guides, interview tips, and QA career learning resources.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Testing Blog", to: "/testing-blog" },
    ],
    toc: [
      { label: "Search Articles", href: "#blog-search" },
      { label: "Latest Articles", href: "#latest-articles" },
    ],
    previous: { label: "Study Notes", to: "/study-notes" },
    next: { label: "Enroll Course", to: "/enroll" },
    related: baseRelated,
    ctas: defaultCtas,
  },
  "/top-regression-testing-tools": {
    eyebrow: "Regression Automation Guide",
    title: "Top Regression Testing Tools and Autonomous Testing Guide",
    description: "Compare Selenium, Playwright, Cypress, Katalon, and AI-assisted autonomous testing tools for practical regression automation.",
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Automation Testing", to: "/automation-testing-tutorial" },
      { label: "Regression Testing Tools", to: "/top-regression-testing-tools" },
    ],
    toc: [
      { label: "Regression Automation", href: "#what-is-it" },
      { label: "Autonomous Testing", href: "#autonomous-testing" },
      { label: "Why Tools Matter", href: "#why-important" },
      { label: "Top Tools", href: "#top-tools" },
      { label: "Tool Comparison", href: "#comparison" },
      { label: "How to Choose", href: "#how-to-choose" },
      { label: "Real Project Example", href: "#example" },
      { label: "Learning Path", href: "#learning-path" },
      { label: "Interview Questions", href: "#interview-questions" },
      { label: "FAQ", href: "#faq" },
    ],
    previous: { label: "Automation Testing Tutorial", to: "/automation-testing-tutorial" },
    next: { label: "Selenium Tutorial", to: "/selenium-tutorial" },
    related: baseRelated,
    ctas: defaultCtas,
  },
};

const topicFlow = topicNavigation.flatMap((group) =>
  group.items.map((item) => ({
    ...item,
    group: group.group,
  }))
);

const manualClusterLayoutConfig = manualTestingClusterPages.reduce((configs, page, index) => {
  const previousPage = manualTestingClusterPages[index - 1];
  const nextPage = manualTestingClusterPages[index + 1];

  configs[page.path] = {
    eyebrow: page.eyebrow,
    title: page.title,
    description: page.description,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Manual Testing", to: "/manual-testing" },
      { label: page.keyword, to: page.path },
    ],
    toc: page.sections.map((section) => ({
      label: section.heading,
      href: `#${section.id}`,
    })),
    previous: previousPage
      ? { label: previousPage.keyword, to: previousPage.path }
      : { label: "Manual Testing", to: "/manual-testing" },
    next: nextPage
      ? { label: nextPage.keyword, to: nextPage.path }
      : { label: "Automation Testing", to: "/automation-testing-topic" },
    related: [...manualClusterRelated, ...baseRelated].filter((topic) => topic.to !== page.path).slice(0, 8),
    ctas: defaultCtas,
  };

  return configs;
}, {});

const automationClusterLayoutConfig = automationTestingClusterPages.reduce((configs, page, index) => {
  const previousPage = automationTestingClusterPages[index - 1];
  const nextPage = automationTestingClusterPages[index + 1];

  configs[page.path] = {
    eyebrow: page.eyebrow,
    title: page.title,
    description: page.description,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Automation Testing", to: "/automation-testing" },
      { label: page.keyword, to: page.path },
    ],
    toc: page.sections.map((section) => ({
      label: section.heading,
      href: `#${section.id}`,
    })),
    previous: previousPage
      ? { label: previousPage.keyword, to: previousPage.path }
      : { label: "Automation Practice", to: "/automation-testing" },
    next: nextPage
      ? { label: nextPage.keyword, to: nextPage.path }
      : { label: "API Testing", to: "/api-testing" },
    related: [...automationClusterRelated, ...baseRelated].filter((topic) => topic.to !== page.path).slice(0, 8),
    ctas: defaultCtas,
  };

  return configs;
}, {});

const apiClusterLayoutConfig = apiTestingClusterPages.reduce((configs, page, index) => {
  const previousPage = apiTestingClusterPages[index - 1];
  const nextPage = apiTestingClusterPages[index + 1];

  configs[page.path] = {
    eyebrow: page.eyebrow,
    title: page.title,
    description: page.description,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "API Testing", to: "/api-testing" },
      { label: page.keyword, to: page.path },
    ],
    toc: page.sections.map((section) => ({
      label: section.heading,
      href: `#${section.id}`,
    })),
    previous: previousPage
      ? { label: previousPage.keyword, to: previousPage.path }
      : { label: "API Testing", to: "/api-testing" },
    next: nextPage
      ? { label: nextPage.keyword, to: nextPage.path }
      : { label: "Testing Interview Questions", to: "/testing-interview-questions" },
    related: [...apiClusterRelated, ...baseRelated].filter((topic) => topic.to !== page.path).slice(0, 8),
    ctas: defaultCtas,
  };

  return configs;
}, {});

const interviewClusterLayoutConfig = interviewQuestionsClusterPages.reduce((configs, page, index) => {
  const previousPage = interviewQuestionsClusterPages[index - 1];
  const nextPage = interviewQuestionsClusterPages[index + 1];

  configs[page.path] = {
    eyebrow: page.eyebrow,
    title: page.title,
    description: page.description,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Interview Questions", to: "/testing-interview-questions" },
      { label: page.keyword, to: page.path },
    ],
    toc: page.sections.map((section) => ({
      label: section.heading,
      href: `#${section.id}`,
    })),
    previous: previousPage
      ? { label: previousPage.keyword, to: previousPage.path }
      : { label: "Study Notes", to: "/study-notes" },
    next: nextPage
      ? { label: nextPage.keyword, to: nextPage.path }
      : { label: "Join Course", to: "/enroll" },
    related: [...interviewClusterRelated, ...baseRelated].filter((topic) => topic.to !== page.path).slice(0, 8),
    ctas: defaultCtas,
  };

  return configs;
}, {});

const practicalExampleLayoutConfig = practicalExampleClusterPages.reduce((configs, page, index) => {
  const previousPage = practicalExampleClusterPages[index - 1];
  const nextPage = practicalExampleClusterPages[index + 1];

  configs[page.path] = {
    eyebrow: page.eyebrow,
    title: page.title,
    description: page.description,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: "Practical Examples", to: "/real-time-testing-scenarios" },
      { label: page.keyword, to: page.path },
    ],
    toc: [
      { label: "Preconditions", href: "#preconditions" },
      { label: "Test Data", href: "#test-data" },
      { label: "Step-by-Step", href: "#step-by-step-example" },
      { label: "Expected Results", href: "#expected-results" },
      { label: "Common Bugs", href: "#common-bugs-to-watch" },
      { label: "Automation Note", href: "#automation-note" },
      { label: "FAQ", href: "#faq" },
    ],
    previous: previousPage
      ? { label: previousPage.keyword, to: previousPage.path }
      : { label: "Manual Testing", to: "/manual-testing" },
    next: nextPage
      ? { label: nextPage.keyword, to: nextPage.path }
      : { label: "Interview Prep", to: "/testing-interview-questions" },
    related: [...practicalExampleRelated, ...baseRelated].filter((topic) => topic.to !== page.path).slice(0, 8),
    ctas: defaultCtas,
  };

  return configs;
}, {});

function getGeneratedTopicConfig(pathname) {
  const topicIndex = topicFlow.findIndex((topic) => topic.to === pathname);

  if (topicIndex === -1) {
    return null;
  }

  const topic = topicFlow[topicIndex];
  const previousTopic = topicFlow[topicIndex - 1];
  const nextTopic = topicFlow[topicIndex + 1];

  return {
    eyebrow: topic.group,
    title: `${topic.label} Learning Guide`,
    description: `Learn ${topic.label.toLowerCase()} with simple explanations, examples, interview-focused notes, and practical QA learning links.`,
    breadcrumbs: [
      { label: "Home", to: "/home" },
      { label: topic.group, to: pathname },
      { label: topic.label, to: pathname },
    ],
    toc: [],
    previous: previousTopic ? { label: previousTopic.label, to: previousTopic.to } : null,
    next: nextTopic ? { label: nextTopic.label, to: nextTopic.to } : null,
    related: baseRelated,
    ctas: defaultCtas,
  };
}

export function getArticleLayoutConfig(pathname) {
  return interviewClusterLayoutConfig[pathname] || practicalExampleLayoutConfig[pathname] || articleLayoutConfig[pathname] || manualClusterLayoutConfig[pathname] || automationClusterLayoutConfig[pathname] || apiClusterLayoutConfig[pathname] || getGeneratedTopicConfig(pathname);
}
